<template>
  <b-container data-cy="user-stored-credit-cards-chooser">
    <square-payment-card-selection-anonymous-error :is-anonymous="isAnonymous" />

    <template v-if="!isAnonymous">
      <b-row v-if="cardDisplay && !storedCardsLoading && creditCardDetails">
        <b-col data-cy="user-selected-card-number">
          <square-payments-storedcard-display
            :card-details="creditCardDetails"
            size="lg"
          />
          <div
            v-if="!viewOnly"
            class="mt-4"
          >
            <b-button
              class="main-change-selected-card"
              data-cy="change-user-seleced-card"
              variant="primary"
              @click="changeSelectedCard"
            >
              <slot>
                Change selected card
              </slot>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-row v-if="cardEntry">
        <b-col>
          <div class="mb-4">
            <square-payments-stored-card-select
              v-model="selectedCard"
              :available-cards="userStoredCards"
              :account="account"
              :allow-other="allowNew"
            >
              <template v-slot:stored-card-header>
                <slot name="stored-card-header" />
              </template>
              <template v-slot:stored-card-message>
                <slot name="stored-card-message" />
              </template>
              <template v-slot:enter-new-card-label>
                <slot name="enter-new-card-label" />
              </template>
            </square-payments-stored-card-select>
          </div>
          <div
            v-if="!storedCardsLoading && !selectedNewCard"
            class="mt-4"
          >
            <b-button
              class="select-user-stored-card"
              variant="primary"
              data-cy="choose-existing-card-button"
              @click="chooseSelectedCard"
            >
              <slot name="select-exisitng-card-button">
                Choose existing stored card
              </slot>
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row v-if="selectedNewCard">
        <b-col>
          <div style="position: relative; min-height: 200px;">
            <square-payments-store-card
              :account="account"
              :options="options"
              :success-component="false"
              :failure-component="false"
              :error-component="false"
              @processing="(val) => $emit('processing', val)"
              @error="storedCardError"
              @success="newStoredCardSelected"
              @loading="handleSquareFormLoading"
            />
            <div
              v-if="squareFormLoading"
              data-cy="square-form-initializing-spinner"
              class="square-form-loading-overlay"
              style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 10; background: rgba(255,255,255,0.95); display: flex; align-items: center; justify-content: center; border-radius: 0.25rem;"
            >
              <loading-spinner>
                <template v-slot:title>
                  Initializing Square Credit Card Form
                </template>
                Please wait while we prepare the secure credit card entry form...
              </loading-spinner>
            </div>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import SquarePaymentsStoreCard from './SquarePaymentsStoreCard.vue'
import SquarePaymentisAnonymousError from '../modals/UserIsAnonymous.vue'
import UserStoredCardSelect from '../components/UserStoredCardSelect.vue'

export default {
  name: 'square-payments-user-stored-card-chooser',
  components: {
    'square-payments-store-card': SquarePaymentsStoreCard,
    'square-payments-stored-card-select': UserStoredCardSelect,
    'square-payment-card-selection-anonymous-error': SquarePaymentisAnonymousError
  },
  props: {
    value: {
      type: String,
      required: true
    },
    account: {
      type: Object,
      required: true
    },
    loadUserStoredCards: {
      type: Boolean,
      required: false,
      default: true
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    allowNew: {
      type: Boolean,
      required: false,
      default: true
    },
    selectFirstCard: {
      type: Boolean,
      required: false,
      default: false
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        requestOptions: {}
      })
    }
  },
  data: function() {
    return {
      mode: 'carddisplay',
      selectedCardVal: '',
      userStoredCards: [],
      storedCardsLoading: false,
      squareFormLoading: false
    }
  },
  computed: {
    creditCardDetails: function() {
      if (this.userStoredCards.length) {
        const cardDetails = this.userStoredCards.find(pm => pm.id === this.value)
        return (cardDetails !== undefined) ? cardDetails : false
      }
      return false
    },
    cardDisplay: function() {
      return this.mode === 'carddisplay'
    },
    cardEntry: function() {
      return this.mode === 'cardentry'
    },
    paymentMethods: function() {
      return this.userStoredCardOptions.map(opt => opt.id)
    },
    userid: function() {
      return this.account.userid || ''
    },
    isAnonymous: function() {
      return this.userid === null || this.userid.trim() === '0' || this.userid.trim() === ''
    },
    hasStoredCards: function() {
      return this.userStoredCards.length > 0
    },
    userStoredCardOptions: function() {
      return this.userStoredCards
    },
    selectedNewCard: function() {
      return this.selectedCardVal === 'new'
    },
    selectedCard: {
      get: function() {
        if (this.userStoredCardOptions.length) {
          return this.selectedCardVal
        }
        return ''
      },
      set: function(val) {
        this.selectedCardVal = val
      }
    }
  },
  watch: {
    selectedNewCard: function(newVal) {
      // When switching to "enter new card", show loading spinner immediately
      if (newVal) {
        this.squareFormLoading = true
      }
      else {
        // When switching away from "enter new card", hide the spinner
        this.squareFormLoading = false
      }
    }
  },
  mounted: function() {
    this.storedCardsLoading = true
    this.init()
      .then(_ => {
        if (this.selectFirstCard && this.hasStoredCards) {
          this.mode = 'cardentry'
          this.selectedCardVal = this.userStoredCards[0].id
          // this.$emit('input', this.selectedCardVal)
        }
        else {
          if (this.userStoredCards.some(pm => pm.id === this.value)) {
            this.mode = 'cardentry'
            this.selectedCardVal = this.value
          }
          else {
            this.mode = 'cardentry'
            this.selectedCardVal = 'new'
          }
        }
      })
      .catch(err => {
        console.error('Error initializing stored cards:', err)
        this.mode = 'cardentry'
        this.selectedCardVal = 'new'
      })
      .finally(() => {
        this.storedCardsLoading = false
      })
  },
  methods: {
    storedCardError: function(resp) {
      this.$emit('error', resp)
    },
    init: function() {
      this.$emit('loading', true)
      let p = Promise.resolve([])
      if (!this.isAnonymous && this.loadUserStoredCards) {
        p = this.$store.dispatch('square/loadCustomerCards', { userid: this.userid })
      }
      return p.then(storedCards => {
        this.userStoredCards = storedCards
      })
        .finally(() => {
          this.$emit('loading', false)
          this.storedCardsLoading = false
        })
    },
    changeSelectedCard: function() {
      this.mode = 'cardentry'
      if (this.userStoredCards.length === 0) {
        this.selectedCardVal = 'new'
      }
      else {
        this.selectedCardVal = this.value
      }
    },
    chooseSelectedCard: function(val) {
      this.$emit('input', this.selectedCardVal)
      this.mode = 'carddisplay'
    },
    newStoredCardSelected: function(val) {
      // First, destroy the Square card form to clean up resources
      this.destroySquareCardForm()

      this.init()
        .then(_ => {
          // Access the card ID from the correct path in the response
          const newCardId = val.card ? val.card.id : (val.payment_method ? val.payment_method.id : null)
          if (newCardId) {
            this.$emit('input', newCardId)
            this.selectedCardVal = newCardId
            this.mode = 'carddisplay'
          }
          else {
            console.warn('Could not find card ID in stored card response:', val)
          }
        })
    },
    destroySquareCardForm: function() {
      // Find and destroy any Square card instances in child components
      const storeCardComponent = this.$children.find(child => child.$options.name === 'square-payments-store-card')
      if (storeCardComponent && typeof storeCardComponent.cleanupSquareForm === 'function') {
        storeCardComponent.cleanupSquareForm()
      }

      // Also call the store action to destroy the Square card if available
      if (this.$store.state.square && this.$store.state.square.squareCard) {
        this.$store.dispatch('square/destroySquareCard')
      }
    },
    handleSquareFormLoading(loading) {
      // console.log('Square form loading state changed:', loading)
      this.squareFormLoading = loading
    }
  }
}
</script>

<style>

</style>
